export enum SignType {
    E_SIGN = 'E_SIGN',
    AADHAAR = 'AADHAAR',
    DSC = 'DSC',
}

/** A platform user returned by the signatory-picker endpoints */
export interface SignatoryUser {
    userId: string;
    name: string;
    email: string;
    orgId?: string;
    orgName?: string;
}

/** Payload for send-email action — maps to POST /magic/sign/sendNotification {type:'REVIEW'} */
export interface SendSigningEmailRequest {
    id: string;
    /** Optional: override recipient emails; backend defaults to signatory emails from the record */
    recipientEmails?: string[];
    /** Custom note (HTML/text) added by sender */
    note?: string;
    /** PNO logo URL for the email template's {{logoUrl}} header */
    logoUrl?: string;
}

/** Payload for send-reminder action — maps to POST /magic/sign/sendNotification {type:'REMINDER'} */
export interface SendReminderRequest {
    id: string;
    /** PNO logo URL for the email template's {{logoUrl}} header */
    logoUrl?: string;
}

/** Payload for global Magic Sign list */
export interface GlobalMagicSignListRequest {
    pnoId: string;
    pageNo: number;
    pageSize: number;
    filters?: MagicSignFilters;
}

/** Payload for associating an org with a global Magic Sign row */
export interface AssociateCompanyRequest {
    id: string;
    orgId: string;
}

/** Extended row for global view, includes org name */
export interface GlobalMagicSignRow extends MagicSignRow {
    orgId?: string;
    orgName?: string;
    orgLogoUrl?: string;
}

/**
 * Payload for POST /magic/sign/create (P2, RECUR-26495).
 * Replaces the old /upload and /sendForSigning endpoints.
 * The doc must already be registered in DocStore via the MU pipeline before calling this.
 */
export interface CreateMagicSignRequest {
    mnemosyneDocId: string;
    /** Optional display filename — falls back to the DocStore row's fileName if absent. */
    fileName?: string;
    /** Optional source hint for audit/tracing. Does not affect behaviour server-side. */
    source?: string;
    /** Optional known org id — passed when the caller already knows the doc's company (RECUR-26495). */
    orgId?: string;
}

/** Key of a colour class exported by statusPill.module.scss — the only place pill colour hex lives. */
export type MagicSignPillTone = 'Neutral' | 'Grey' | 'Blue' | 'LightGreen' | 'Green' | 'Red' | 'Amber';

export enum MagicSignStatus {
    UPLOADING = 'UPLOADING',
    UPLOADED = 'UPLOADED',
    /** Client-only transient state shown while signing links are being generated (never returned by the backend). */
    GENERATING_LINKS = 'GENERATING_LINKS',
    GENERATED_LINKS = 'GENERATED_LINKS',
    PARTIAL_SIGNED = 'PARTIAL_SIGNED',
    ALL_SIGNED = 'ALL_SIGNED',
    CANCELLED = 'CANCELLED',
}

export enum SignStatus {
    PENDING = 'PENDING',
    SIGNED = 'SIGNED',
}

export interface Signatory {
    /**
     * Real OrganisationUsers id. Required — signatories must be selected from the existing-borrower
     * picker (fetchMagicSignCompanyUsers / fetchMagicSignPnoUsers); the backend rejects any
     * signatory that doesn't carry a userId, so there is no inline name/email-only signatory shape.
     */
    userId: string;
    name: string;
    email: string;
    orgName?: string;
    signingCapacity?: string;
    signStatus?: SignStatus;
    signedAt?: string;
    /** Per-signatory signing URL returned by the backend once links are generated */
    signingLink?: string;
}

export interface MagicSignRow {
    id: string;
    orgId?: string;
    fileName: string;
    documentType?: string;
    signType?: SignType;
    signatories?: Signatory[];
    status: MagicSignStatus;
    requestedByName?: string;
    /** Platform user id of the requester — used as the value for the Requested By filter */
    requestedByUserId?: string;
    updatedAt?: string;
    mnemosyneDocId?: string;
    signedDocId?: string;
    /** Mnemosyne id of the fully-signed PDF (set once All Signed) — returned by the backend DTO. */
    signedMnemosyneDocId?: string;
    signingLinks?: { signatoryId: string; link: string; name: string; orgName?: string }[];
    /** ISO timestamp of when the signing email was first sent; null/absent means email not yet sent */
    emailSentAt?: string | null;
}

export interface MagicSignFilters {
    statuses?: MagicSignStatus[];
    documentTypes?: string[];
    signTypes?: SignType[];
    requestedBy?: string[];
    /** Global view: filter by organisation (assessee org ids) */
    orgIds?: string[];
    /** Filter by signatory email (multi-select) */
    signatoryEmails?: string[];
    sortBy?: 'updatedAt' | 'fileName' | 'status';
    sortDir?: 'asc' | 'desc';
}

export interface MagicSignListRequest {
    pnoId: string;
    orgId: string;
    pageNo: number;
    pageSize: number;
    filters?: MagicSignFilters;
}

export interface MagicSignConfigRequest {
    id: string;
    documentType?: string;
    signType?: SignType;
    signatories?: Signatory[];
}

export interface AuditLogEvent {
    event: string;
    actorName?: string;
    actorEmail?: string;
    actorOrg?: string;
    timestamp: number | string;
    ip?: string;
    latitude?: number;
    longitude?: number;
}

/** Raw shape returned by the backend /magic/sign/config-meta endpoint */
export interface ConfigMetaRaw {
    enabledSignTypes?: string[];
    documentTypes?: string[];
    signingCapacities?: string[];
    maxSignatories?: number;
    statusLabels?: Partial<Record<MagicSignStatus, string>>;
}

/** Normalised shape consumed by the FE after mapping string arrays → {id,label}[] */
export interface ConfigMeta {
    documentTypes: { id: string; label: string }[];
    /** Only sign types present in enabledSignTypes from backend are included */
    signTypes: { id: SignType; label: string; enabled: boolean }[];
    statusConfig?: Partial<Record<MagicSignStatus, { label: string }>>;
    maxSignatories?: number;
    signingCapacities?: { id: string; label: string }[];
}

// Public portal types
export interface PublicSignInfo {
    token: string;
    status: 'ACTIVE' | 'EXPIRED' | 'CANCELLED' | 'ALREADY_SIGNED';
    documentName?: string;
    pnoLogo?: string;
    pnoName?: string;
    companyName?: string;
    signatoryName?: string;
    signatoryEmail?: string;
    actionLabel?: string;
    consentTemplate?: string;
    expiresAt?: string;
}

export interface OtpVerifyResponse {
    /** Design A (RECUR-26495): cerberus JWT returned after OTP verify; used as x-cerberus-token on /public/sign. */
    authToken: string;
}

// Note: the signed confirmation is shown inline on the 'document' step (signed view + download CTA),
// so there is no separate 'success' step.
export type PublicSignPortalStep = 'loading' | 'unavailable' | 'otp' | 'document' | 'sign';

export type MagicSignSortBy = 'updatedAt' | 'fileName' | 'status';
export type MagicSignSortDir = 'asc' | 'desc';
